import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Table as TableIcon,
  MapPin,
  Clock,
  Users,
  Loader2,
  Lock,
  GitBranch,
  ClipboardList,
  Trophy,
  Medal,
  BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { buildBracketModel, describeEvent } from '@/lib/bracket/model';
import { statsLinksFor } from '@/lib/bracket/stats';
import { isCricket } from '@/lib/cricket';
import { CricketScore } from '@/components/schedule/CricketScore';
import { shareHero } from '@/lib/shareTheme';
import { TeamRosterCards } from '@/components/schedule/TeamRosterCards';
import { TeamStatsDialog } from '@/components/schedule/TeamStatsDialog';
import { StandingsTable } from '@/components/schedule/StandingsTable';
import { PublicLeaderboardView } from '@/components/schedule/PublicLeaderboardView';
import { PublicSeasonStats } from '@/components/schedule/PublicSeasonStats';
import { SportLeaderboard } from '@/components/schedule/SportLeaderboard';
import { FIELD_SPORTS } from '@/lib/sportStats';

// Whitelist of valid tab path segments. Anything else falls back to the
// default tab. Used both for URL → state hydration and state → URL writing.
const VALID_TABS = new Set([
  'table', 'calendar', 'standings', 'leaderboard', 'season-stats', 'teams', 'bracket',
]);

export default function SharedScheduleView() {
  const { shareToken, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initial view is hydrated from the URL path segment when present.
  const [view, setViewState] = useState(VALID_TABS.has(tabParam) ? tabParam : 'table');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedTeam, setSelectedTeam] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const isBracketFormat = ['single_elimination', 'double_elimination', 'consolation'].includes(event?.format);
  // Basketball uses the /stats live-tracker pipeline (stats_games) — every
  // other sport uses the sport_box_scores collection surfaced via the
  // /api/events/{event_id}/leaderboard + /box-score-image endpoints. We
  // branch UI here so football/volleyball/etc. get their own leaderboard
  // and clickable box-score icon on the schedule table.
  const isBasketball = (event?.sport || '').toLowerCase() === 'basketball' || !event?.sport;
  // Set of match IDs that have a saved non-basketball box score — used to
  // decide whether to render the clipboard icon in the score column.
  const sportBoxScoreMatchIds = useMemo(() => {
    const s = new Set();
    (event?.sport_box_score_match_ids || []).forEach(id => s.add(id));
    return s;
  }, [event]);

  // Replace the URL whenever the tab changes so the address bar always
  // matches the visible tab — and each tab becomes independently shareable
  // by copy/pasting the URL.
  const setView = useCallback((newView) => {
    setViewState(newView);
    const path = newView === 'table'
      ? `/share/${shareToken}`        // canonical default — no /table suffix
      : `/share/${shareToken}/${newView}`;
    navigate(path, { replace: false });
  }, [shareToken, navigate]);

  // Hydrate `view` state from URL changes (back/forward navigation).
  useEffect(() => {
    const next = VALID_TABS.has(tabParam) ? tabParam : 'table';
    setViewState(next);
  }, [tabParam]);

  // Set default view to bracket for bracket formats (but only when no
  // explicit tab is in the URL — respect the URL if the user landed on
  // /share/TOKEN/standings directly).
  useEffect(() => {
    if (event && isBracketFormat && !tabParam) {
      setViewState('bracket');
      navigate(`/share/${shareToken}/bracket`, { replace: true });
    }
  }, [event, isBracketFormat, tabParam, shareToken, navigate]);

  const bracketModel = useMemo(
    () => (event?.schedule && isBracketFormat ? buildBracketModel({ format: event.format, schedule: event.schedule }) : null),
    [event, isBracketFormat],
  );
  const bracketStatsLinks = useMemo(() => (isBracketFormat ? statsLinksFor(event, shareToken) : null), [event, isBracketFormat, shareToken]);

  useEffect(() => {
    const fetchSharedEvent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/share/${shareToken}`);
        if (!response.ok) {
          throw new Error('Schedule not found or link has expired');
        }
        const data = await response.json();
        setEvent(data);
        if (data.start_date) {
          setCalendarMonth(parseISO(data.start_date));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchSharedEvent();
    }
  }, [shareToken, API_URL]);

  // Dynamically inject OG / Twitter meta tags pointing at the promo image endpoint
  // so when this share link is pasted into iMessage/Slack/Twitter (or shared from a
  // browser session that already loaded it), the rich preview shows the promo card.
  useEffect(() => {
    if (!shareToken || !event) return;
    const promoUrl = `${API_URL}/api/share/${shareToken}/promo-image?size=og`;
    const desc = `${event.name || 'Tournament'}${event.start_date ? ` — ${event.start_date}` : ''}`;
    const upsert = (selector, attrs) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
        document.head.appendChild(tag);
      } else {
        Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
      }
      return tag;
    };
    const previousTitle = document.title;
    document.title = `${event.name || 'Tournament'} — Schedule Guru`;
    const tags = [
      upsert('meta[property="og:title"]', { property: 'og:title', content: event.name || 'Tournament' }),
      upsert('meta[property="og:description"]', { property: 'og:description', content: desc }),
      upsert('meta[property="og:image"]', { property: 'og:image', content: promoUrl }),
      upsert('meta[property="og:type"]', { property: 'og:type', content: 'website' }),
      upsert('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' }),
      upsert('meta[name="twitter:title"]', { name: 'twitter:title', content: event.name || 'Tournament' }),
      upsert('meta[name="twitter:description"]', { name: 'twitter:description', content: desc }),
      upsert('meta[name="twitter:image"]', { name: 'twitter:image', content: promoUrl }),
    ];
    return () => {
      document.title = previousTitle;
      // Reset to defaults rather than removing — keeps the SPA's default branding
      tags.forEach(t => t.parentNode && t.parentNode.removeChild(t));
    };
  }, [shareToken, event, API_URL]);

  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  const getMatchesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return event?.schedule?.filter(m => m.date === dateStr) || [];
  };

  const getStageBadgeClass = (stage) => {
    if (!stage) return 'bg-slate-100 text-slate-600';
    if (stage.includes('RR')) return 'bg-blue-100 text-blue-700';
    if (stage.includes('F') && !stage.includes('SF') && !stage.includes('QF')) return 'bg-amber-100 text-amber-700';
    if (stage.includes('SF') || stage.includes('QF')) return 'bg-purple-100 text-purple-700';
    if (stage.includes('LB')) return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Schedule Not Found</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-[#00247D] hover:bg-[#001A5C]">
                Go to Schedule Guru
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const homeAwayEnabled = event?.rules?.home_away_enabled;
  const hasVenues = event?.venues && event.venues.length > 0;
  const hero = shareHero(event?.share_theme);
  const accentIcon = { color: hero.accent, opacity: 0.85 };
  const tabStyle = (v) => (view === v ? hero.activeTab : undefined);

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Hero — organizer-chosen header + accent colours (default navy-to-slate with amber) */}
      <header className="border-b-4 sticky top-0 z-10 shadow-xl" style={hero.header} data-testid="shared-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              {event.organizer_organization && (
                <div className="mb-2">
                  <div
                    className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 max-w-full ${hero.pill}`}
                    data-testid="organizer-org-pill"
                  >
                    {event.organizer_organization_logo && (
                      <img
                        src={`${API_URL}/api/files/${event.organizer_organization_logo}`}
                        alt=""
                        className="w-6 h-6 rounded-sm bg-white object-contain shrink-0"
                        data-testid="organizer-org-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="text-xs font-semibold leading-snug whitespace-normal text-left">
                      {event.organizer_organization}
                    </span>
                  </div>
                </div>
              )}
              <h1
                className="text-3xl sm:text-5xl font-black leading-none tracking-wide"
                style={{ fontFamily: 'Barlow Condensed', color: hero.accent }}
                data-testid="shared-hero-title"
              >
                {event.name?.toUpperCase()}
              </h1>
              <div className={`flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm ${hero.text}`}>
                <div className="flex items-center gap-1.5">
                  <CalendarIcon className="w-4 h-4" style={accentIcon} />
                  <span>{event.start_date ? format(parseISO(event.start_date), 'MMM d, yyyy') : 'TBD'}</span>
                </div>
                {event.venue && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" style={accentIcon} />
                    <span>{event.venue}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" style={accentIcon} />
                  <span>{event.teams?.length || 0} teams</span>
                </div>
              </div>
            </div>
            {/* Top-right logo strip: Event logo + Organization logo, both with a
                white-tinted halo on the dark hero so colored crests pop. */}
            {(event.event_logo || event.organizer_organization_logo) && (
              <div className="flex items-center justify-end gap-3 shrink-0" data-testid="shared-hero-logos">
                {event.event_logo && (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white p-1 shadow-lg shadow-black/30 ring-2 flex items-center justify-center overflow-hidden"
                    style={{ '--tw-ring-color': hero.accent }}>
                    <img
                      src={`${API_URL}/api/files/${event.event_logo}`}
                      alt=""
                      className="w-full h-full object-contain rounded-full"
                      data-testid="shared-hero-event-logo"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  </div>
                )}
                {event.organizer_organization_logo && (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white p-1 shadow-lg shadow-black/30 ring-2 ring-white/30 flex items-center justify-center overflow-hidden">
                    <img
                      src={`${API_URL}/api/files/${event.organizer_organization_logo}`}
                      alt=""
                      className="w-full h-full object-contain rounded-full"
                      data-testid="shared-hero-org-logo-big"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 py-3 shadow-sm sticky top-[120px] sm:top-[112px] z-[5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            {/* Horizontal-scroll wrapper so the 6–7 tabs don't push the whole
                page wider than the viewport on phones. The negative margin +
                padding lets the scroll area visually "bleed" to the screen edge
                so partial-tab-cutoff hints at more content. */}
            <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar">
              <Tabs value={view} onValueChange={setView}>
                <TabsList className="bg-slate-100 inline-flex w-max">
                  {isBracketFormat && (
                    <TabsTrigger value="bracket" className="flex items-center gap-2 shrink-0" style={tabStyle('bracket')} data-testid="shared-bracket-tab">
                      <GitBranch className="w-4 h-4" />
                      Bracket
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="table" className="flex items-center gap-2 shrink-0" style={tabStyle('table')} data-testid="shared-table-tab">
                    <TableIcon className="w-4 h-4" />
                    Table
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="flex items-center gap-2 shrink-0" style={tabStyle('calendar')} data-testid="shared-calendar-tab">
                    <CalendarIcon className="w-4 h-4" />
                    Calendar
                  </TabsTrigger>
                  <TabsTrigger value="standings" className="flex items-center gap-2 shrink-0" style={tabStyle('standings')} data-testid="shared-standings-tab">
                    <Medal className="w-4 h-4" />
                    Standings
                  </TabsTrigger>
                  <TabsTrigger value="leaderboard" className="flex items-center gap-2 shrink-0" style={tabStyle('leaderboard')} data-testid="shared-leaderboard-tab">
                    <Trophy className="w-4 h-4" />
                    Leaderboard
                  </TabsTrigger>
                  <TabsTrigger value="season-stats" className="flex items-center gap-2 shrink-0" style={tabStyle('season-stats')} data-testid="shared-season-stats-tab">
                    <BarChart3 className="w-4 h-4" />
                    Season Stats
                  </TabsTrigger>
                  {(event?.team_rosters || []).length > 0 && (
                    <TabsTrigger value="teams" className="flex items-center gap-2 shrink-0" style={tabStyle('teams')} data-testid="shared-teams-tab">
                      <Users className="w-4 h-4" />
                      Teams
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>
            <Badge variant="outline" className="text-slate-500 self-end sm:self-auto shrink-0">
              Read-only
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {view === 'standings' ? (
          <div data-testid="shared-standings-view-wrap">
            <div className="mb-4 flex items-center gap-2">
              <Medal className="w-5 h-5 text-[#00247D]" />
              <h2 className="text-lg font-bold text-[#00247D] uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>
                Standings
              </h2>
            </div>
            <StandingsTable standings={event?.standings || {}} teams={event?.teams || []} teamLogos={event?.team_logos || {}} shareToken={shareToken} labels={event?.sport_meta?.score_labels || {}} tieBreakers={event?.sport_meta?.tie_breakers || []} headStyle={hero.tableHead} />
          </div>
        ) : view === 'leaderboard' ? (
          <div data-testid="shared-leaderboard-view-wrap">
            <div className="mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-bold text-[#00247D] uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>
                Leaderboard
              </h2>
            </div>
            {isBasketball ? (
              <PublicLeaderboardView
                shareToken={shareToken}
                eventId={event?.id}
                leaderboardShareToken={event?.leaderboard_share_token || ''}
                eventTeams={event?.teams || []}
              />
            ) : (
              // Non-basketball events use the sport_box_scores aggregator.
              // Public read is allowed for public events, so no auth needed.
              <SportLeaderboard eventId={event?.id} />
            )}
          </div>
        ) : view === 'season-stats' ? (
          <div data-testid="shared-season-stats-view-wrap">
            <PublicSeasonStats
              shareToken={shareToken}
              eventId={event?.id}
              leaderboardShareToken={event?.leaderboard_share_token || ''}
              eventTeams={event?.teams || []}
              sport={event?.sport || 'basketball'}
            />
          </div>
        ) : view === 'teams' ? (
          <div data-testid="shared-teams-view">
            <div className="mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00247D]" />
              <h2 className="text-lg font-bold text-[#00247D] uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>
                Tournament Teams
              </h2>
              <span className="text-sm text-slate-500">· {(event?.team_rosters || []).length} submitted · click any card for season stats</span>
            </div>
            <TeamRosterCards rosters={event?.team_rosters || []} onTeamClick={setSelectedTeam} />
          </div>
        ) : view === 'bracket' && isBracketFormat ? (
          <div data-testid="shared-bracket-view">
            {bracketModel ? (
              <BracketBoard model={bracketModel} title={event?.name?.toUpperCase()} subtitle={describeEvent(event)}
                eventLogo={event?.event_logo ? `${API_URL}/api/files/${event.event_logo}` : null}
                logos={event?.team_logos || {}} editable={false} storageKey={`bracket-layout:share:${shareToken}`} statsLinks={bracketStatsLinks}
                fileName={`${(event?.name || 'bracket').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-bracket`}
                showCourt={(event?.num_courts || 1) > 1} />
            ) : (
              <div className="py-12 text-center text-slate-500">No bracket data available</div>
            )}
          </div>
        ) : view === 'table' ? (
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={hero.tableHead} data-testid="shared-table-head">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Time</th>
                    {hasVenues && <th className="px-4 py-3 text-center">Venue</th>}
                    <th className="px-4 py-3 text-center">{FIELD_SPORTS.includes((event?.sport || '').toLowerCase()) ? 'Field' : 'Court'}</th>
                    <th className="px-4 py-3 text-center">Stage</th>
                    <th className="px-4 py-3 text-right">
                      {homeAwayEnabled ? 'Home' : 'Team A'}
                    </th>
                    <th className="px-4 py-3 text-center">Score</th>
                    <th className="px-4 py-3 text-left">
                      {homeAwayEnabled ? 'Away' : 'Team B'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {event.schedule?.length === 0 ? (
                    <tr>
                      <td colSpan={hasVenues ? 8 : 7} className="px-4 py-8 text-center text-slate-500">
                        No matches scheduled
                      </td>
                    </tr>
                  ) : (
                    event.schedule?.map((match, i) => {
                      const isHomeTeamA = match.is_home_team_a;
                      let leftTeam, rightTeam, leftLabel, rightLabel, leftTeamId, rightTeamId;
                      
                      if (homeAwayEnabled && isHomeTeamA !== null && isHomeTeamA !== undefined) {
                        if (isHomeTeamA) {
                          leftTeam = match.team_a_name || 'TBD';
                          rightTeam = match.team_b_name || 'TBD';
                          leftTeamId = match.team_a;
                          rightTeamId = match.team_b;
                          leftLabel = 'H';
                          rightLabel = 'A';
                        } else {
                          leftTeam = match.team_b_name || 'TBD';
                          rightTeam = match.team_a_name || 'TBD';
                          leftTeamId = match.team_b;
                          rightTeamId = match.team_a;
                          leftLabel = 'H';
                          rightLabel = 'A';
                        }
                      } else {
                        leftTeam = match.team_a_name || 'TBD';
                        rightTeam = match.team_b_name || 'TBD';
                        leftTeamId = match.team_a;
                        rightTeamId = match.team_b;
                        leftLabel = null;
                        rightLabel = null;
                      }
                      const leftLogo = event?.team_logos?.[leftTeamId];
                      const rightLogo = event?.team_logos?.[rightTeamId];

                      return (
                        <tr 
                          key={match.id} 
                          className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                        >
                          <td className="px-4 py-3">
                            <span className="font-medium text-slate-700">
                              {match.date ? format(parseISO(match.date), 'MMM d') : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-600">{match.time || '-'}</span>
                          </td>
                          {hasVenues && (
                            <td className="px-4 py-3 text-center">
                              <span className="text-slate-600 text-sm">
                                {match.venue || '-'}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline" className="font-mono">
                              {match.court}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={getStageBadgeClass(match.stage)}>
                              {match.match_label || match.stage || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {leftLabel && (
                                <Badge className="bg-green-100 text-green-700 text-xs px-1.5">
                                  {leftLabel}
                                </Badge>
                              )}
                              {leftLogo && (
                                <img src={`${API_URL}/api/files/${leftLogo}`} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display='none'; }} />
                              )}
                              <span className="font-semibold text-slate-800">
                                {leftTeam}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const hasScore = match.score_a !== null && match.score_a !== undefined && match.score_b !== null && match.score_b !== undefined;
                              const leftScore = isHomeTeamA === false ? match.score_b : match.score_a;
                              const rightScore = isHomeTeamA === false ? match.score_a : match.score_b;
                              const statsLink = event?.stats_game_by_match?.[match.id];
                              const isVolleyballShare = (event?.sport || '').toLowerCase() === 'volleyball';
                              const isCricketShare = isCricket(event?.sport);
                              // For volleyball, order set breakdown per the
                              // isHomeTeamA flag so the left-side team's
                              // points always show on the left.
                              const setBreakdown = isVolleyballShare && Array.isArray(match.sets)
                                ? match.sets
                                    .filter(s => s.a !== null && s.a !== undefined && s.b !== null && s.b !== undefined)
                                    .map(s => isHomeTeamA === false ? `${s.b}-${s.a}` : `${s.a}-${s.b}`)
                                    .join(', ')
                                : '';
                              return (
                                <div className="flex flex-col items-center gap-0.5">
                                <div className="flex items-center justify-center gap-1.5">
                                  {hasScore ? (
                                    <div className={`font-bold font-mono tabular-nums text-sm px-2 py-0.5 rounded ${leftScore > rightScore ? 'text-[#00247D]' : leftScore < rightScore ? 'text-slate-400' : 'text-slate-600'}`}>
                                      {isCricketShare ? <CricketScore match={match} side={isHomeTeamA === false ? 'b' : 'a'} testId={`public-cricket-left-${match.id}`} /> : leftScore}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-xs">vs</span>
                                  )}
                                  {hasScore && <span className="text-slate-300">-</span>}
                                  {hasScore && (
                                    <div className={`font-bold font-mono tabular-nums text-sm px-2 py-0.5 rounded ${rightScore > leftScore ? 'text-[#00247D]' : rightScore < leftScore ? 'text-slate-400' : 'text-slate-600'}`}>
                                      {isCricketShare ? <CricketScore match={match} side={isHomeTeamA === false ? 'a' : 'b'} testId={`public-cricket-right-${match.id}`} /> : rightScore}
                                    </div>
                                  )}
                                  {statsLink && (
                                    <a
                                      href={`/stats/summary/${statsLink.id}?share=${shareToken}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#00247D]/10 text-[#00247D] transition-colors"
                                      title="View box score"
                                      data-testid={`public-box-score-${match.id}`}>
                                      <ClipboardList className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                  {!statsLink && !isBasketball && sportBoxScoreMatchIds.has(match.id) && (
                                    // Non-basketball sports: link to the
                                    // sport-aware summary page instead of
                                    // the generated PNG so fans get the
                                    // same interactive experience as the
                                    // basketball recap page.
                                    <a
                                      href={`/stats/match/${match.id}?event=${event.id}${shareToken ? `&share=${shareToken}` : ''}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#00247D]/10 text-[#00247D] transition-colors"
                                      title="View box score"
                                      data-testid={`public-sport-box-score-${match.id}`}>
                                      <ClipboardList className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                </div>
                                {setBreakdown && (
                                  <div className="text-[10px] text-slate-400 font-mono tracking-wide" data-testid={`public-set-breakdown-${match.id}`}>
                                    {setBreakdown}
                                  </div>
                                )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <div className="flex items-center gap-2">
                              {rightLogo && (
                                <img src={`${API_URL}/api/files/${rightLogo}`} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display='none'; }} />
                              )}
                              <span className="font-semibold text-slate-800">
                                {rightTeam}
                              </span>
                              {rightLabel && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs px-1.5">
                                  {rightLabel}
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <div>
            {/* Calendar Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h3 className="text-lg font-semibold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                {format(calendarMonth, 'MMMM yyyy')}
              </h3>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="grid grid-cols-7 gap-px bg-slate-200">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="bg-slate-100 px-2 py-3 text-center text-sm font-medium text-slate-600">
                    {day}
                  </div>
                ))}
                {/* Leading spacers so day 1 lands in its weekday column */}
                {Array.from({ length: calendarDays[0]?.getDay() || 0 }).map((_, i) => (
                  <div key={`empty-lead-${i}`} className="bg-slate-50 min-h-[100px]" />
                ))}
                {calendarDays.map(day => {
                  const dayMatches = getMatchesForDay(day);
                  const hasGames = dayMatches.length > 0;
                  return (
                    <div 
                      key={day.toISOString()} 
                      className={`bg-white min-h-[100px] p-2 ${hasGames ? 'bg-blue-50' : ''}`}
                    >
                      <div className={`text-sm font-medium mb-2 ${
                        isSameDay(day, new Date()) ? 'text-red-600' : 'text-slate-700'
                      }`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1 overflow-y-auto max-h-20">
                        {dayMatches.slice(0, 3).map((match) => (
                          <div 
                            key={match.id}
                            className="bg-[#00247D]/10 rounded px-1 py-0.5 text-xs text-[#00247D] truncate"
                            title={`${match.team_a_name} vs ${match.team_b_name} @ ${match.time}`}
                          >
                            {match.time} - {match.team_a_name?.substring(0, 6)} vs {match.team_b_name?.substring(0, 6)}
                          </div>
                        ))}
                        {dayMatches.length > 3 && (
                          <div className="text-xs text-slate-500">
                            +{dayMatches.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Total Matches</div>
              <div className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                {event.schedule?.length || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Teams</div>
              <div className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                {event.teams?.length || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">{FIELD_SPORTS.includes((event?.sport || '').toLowerCase()) ? 'Fields' : 'Courts'}</div>
              <div className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                {event.num_courts || 1}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Format</div>
              <div className="text-lg font-bold text-[#00247D] capitalize" style={{ fontFamily: 'Barlow Condensed' }}>
                {event.format?.replace(/_/g, ' ') || '-'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer with Logo */}
        <div className="mt-8 py-8 border-t border-slate-200 bg-white">
          <div className="flex flex-col items-center gap-4">
            {event.organizer_organization && (
              <div className="text-center" data-testid="organizer-credit">
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Hosted by</p>
                <p className="text-base font-bold text-slate-700 mt-0.5">{event.organizer_organization}</p>
              </div>
            )}
            <Link to="/" className="flex flex-col items-center gap-2">
              <img 
                src="/logo-transparent.png" 
                alt="Schedule Guru" 
                className="h-16 object-contain"
                data-testid="footer-logo"
              />
              <span 
                className="text-2xl font-bold text-[#00247D] tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                SCHEDULE GURU
              </span>
            </Link>
            <p className="text-sm text-slate-500 text-center">
              Professional League & Tournament Scheduling
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00247D] text-white text-sm font-medium rounded-lg hover:bg-[#001A5C] transition-colors"
              data-testid="create-schedule-btn"
            >
              Create Your Own Schedule
            </Link>
            <p className="text-xs text-slate-400 mt-2">
              Powered by{' '}
              <Link to="/" className="text-[#00247D] font-medium hover:underline">
                scheduleguru.ws
              </Link>
            </p>
          </div>
        </div>
      </main>
      <TeamStatsDialog
        shareToken={shareToken}
        teamId={selectedTeam?.team_id}
        open={!!selectedTeam}
        onOpenChange={(open) => { if (!open) setSelectedTeam(null); }}
        leaderboardShareToken={event?.leaderboard_share_token || ''}
        eventId={event?.id || ''}
      />
    </div>
  );
}
